import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

export default function Home() {
  const [searchQuery, setSearchQuery] = useState('');
  const [propertyType, setPropertyType] = useState('all');
  const [priceRange, setPriceRange] = useState('all');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://bellcrestproperties.com';

  const whatsappNumber = "+254748356920";
  const whatsappMessage = "Hello Bellcrest Properties, I'm interested in the Kamakis plot for KSh 3.5M.";
  const whatsappUrl = `https://wa.me/${whatsappNumber.replace('+', '')}?text=${encodeURIComponent(whatsappMessage)}`;

  const generalWhatsappMessage = "Hello Bellcrest Properties, I'm interested in your real estate services.";
  const generalWhatsappUrl = `https://wa.me/${whatsappNumber.replace('+', '')}?text=${encodeURIComponent(generalWhatsappMessage)}`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "Bellcrest Properties",
    "description": "Premium real estate services in Kenya specializing in luxury properties, land sales, and property consultation",
    "url": `${siteUrl}/`,
    "telephone": "+254748356920",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "KE",
      "addressRegion": "Nairobi"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "-1.286389",
      "longitude": "36.817223"
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Nairobi"
      },
      {
        "@type": "City", 
        "name": "Mombasa"
      },
      {
        "@type": "Country",
        "name": "Kenya"
      }
    ],
    "serviceType": [
      "Real Estate Sales",
      "Property Consultation", 
      "Land Sales",
      "Property Investment Advisory"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Bellcrest Properties - Premium Real Estate Kenya | Land Sales Nairobi"
        description="Bellcrest Properties offers premium real estate services in Kenya. Find luxury properties, land for sale in Nairobi, Kamakis plots, and expert property consultation. Contact +254748356920 for guaranteed high returns on real estate investment."
        keywords="real estate Kenya, land for sale Nairobi, Bellcrest Properties, Kamakis plots, Kenya property investment, luxury properties Kenya, real estate consultation Kenya"
        canonicalUrl={`${siteUrl}/`}
        ogTitle="Bellcrest Properties - Premium Real Estate Kenya"
        ogDescription="Find luxury properties and land for sale in Kenya. Expert real estate consultation with guaranteed high returns. Contact Bellcrest Properties today."
        ogImage="https://public.readdy.ai/ai/img_res/cd31d80025fe8ec7fa16ee0814aa2271.jpg"
        structuredData={structuredData}
      />

      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-amber-600" style={{ fontFamily: "Pacifico, serif" }}>
                Bellcrest Properties
              </h1>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <a href="/" className="text-gray-900 hover:text-amber-600 px-3 py-2 rounded-md text-sm font-medium">Home</a>
                <a href="/trends" className="text-gray-500 hover:text-amber-600 px-3 py-2 rounded-md text-sm font-medium">Market Trends</a>
                <a 
                  href="https://wa.me/254748356920?text=Hello%20Bellcrest%20Properties,%20I%20would%20like%20to%20inquire%20about%20your%20real%20estate%20services"
                  className="bg-amber-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-amber-700 whitespace-nowrap cursor-pointer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Modern%20luxury%20residential%20estate%20in%20Kenya%20with%20beautiful%20landscaping%2C%20contemporary%20architecture%2C%20and%20clear%20blue%20sky.%20The%20scene%20shows%20elegant%20homes%20with%20manicured%20gardens%2C%20wide%20streets%2C%20and%20a%20sense%20of%20prosperity.%20Professional%20real%20estate%20photography%20style%20with%20warm%20lighting%20and%20inviting%20atmosphere.%20High%20quality%2C%20detailed%2C%20and%20aspirational%20residential%20development%20in%20East%20Africa&width=1920&height=1080&seq=hero-bg&orientation=landscape')`
        }}
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Your Trusted Real Estate Partner
            </h2>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              Bellcrest Properties connects you with the best real estate opportunities across Kenya. 
              From land sales to property consultation, we make your investment dreams a reality.
            </p>
            <a 
              href="https://wa.me/254748356920?text=Hello%20Bellcrest%20Properties,%20I%20would%20like%20to%20learn%20more%20about%20your%20real%20estate%20services"
              className="bg-amber-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-amber-700 transition duration-300 inline-block cursor-pointer whitespace-nowrap"
              target="_blank"
              rel="noopener noreferrer"
            >
              Start Your Journey
            </a>
          </div>
        </div>
      </section>

      {/* Featured Property */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Featured Property</h3>
            <p className="text-lg text-gray-600">Prime investment opportunity available now</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-4xl mx-auto">
            <div className="md:flex">
              <div className="md:w-1/2">
                <img 
                  src="https://readdy.ai/api/search-image?query=Beautiful%20residential%20plot%20of%20land%20in%20Kamakis%20Kenya%20ready%20for%20development.%20The%20plot%20shows%20clear%20boundaries%2C%20good%20access%20road%2C%20and%20surrounding%20residential%20development.%20Green%20vegetation%2C%20clear%20sky%2C%20and%20well-maintained%20infrastructure.%20Professional%20real%20estate%20photography%20showing%20investment%20potential%20and%20prime%20location%20for%20building%20a%20home&width=600&height=400&seq=kamakis-plot&orientation=landscape"
                  alt="Kamakis Plot for Sale - 40x80 feet"
                  className="w-full h-64 md:h-full object-cover object-top"
                />
              </div>
              <div className="md:w-1/2 p-8">
                <div className="mb-4">
                  <span className="bg-amber-100 text-amber-800 text-sm font-medium px-3 py-1 rounded-full">
                    Prime Location
                  </span>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">
                  <a href="#kamakis-plot" className="hover:text-amber-600">Kamakis Residential Plot</a>
                </h4>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-gray-600">
                    <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center mr-3 text-amber-600"></i>
                    <span>Kamakis, Kiambu County</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <i className="ri-ruler-line w-5 h-5 flex items-center justify-center mr-3 text-amber-600"></i>
                    <span>40 x 80 feet (0.07 acres)</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <i className="ri-file-text-line w-5 h-5 flex items-center justify-center mr-3 text-amber-600"></i>
                    <span>Ready Title Deed</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <i className="ri-road-map-line w-5 h-5 flex items-center justify-center mr-3 text-amber-600"></i>
                    <span>Good Access Road</span>
                  </div>
                </div>
                <div className="mb-6">
                  <span className="text-3xl font-bold text-amber-600">KSh 3,500,000</span>
                </div>
                <a 
                  href="https://wa.me/254748356920?text=Hello%20Bellcrest%20Properties,%20I%20am%20interested%20in%20the%20Kamakis%20plot%20for%20KSh%203,500,000.%20Please%20provide%20more%20details."
                  className="w-full bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-amber-700 transition duration-300 inline-block text-center cursor-pointer whitespace-nowrap"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Inquire on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Real Estate Services</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Bellcrest Properties offers comprehensive real estate solutions to help you make informed investment decisions
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-4-line w-8 h-8 flex items-center justify-center text-amber-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                <a href="#property-sales" className="hover:text-amber-600">Property Sales</a>
              </h4>
              <p className="text-gray-600">
                Find your dream home or investment property with our extensive portfolio of residential and commercial properties across Kenya.
              </p>
            </div>
            
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-landscape-line w-8 h-8 flex items-center justify-center text-amber-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                <a href="#land-sales" className="hover:text-amber-600">Land Sales & Consultation</a>
              </h4>
              <p className="text-gray-600">
                Secure prime plots for development with our expert guidance on location, pricing, and legal documentation.
              </p>
            </div>
            
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-key-2-line w-8 h-8 flex items-center justify-center text-amber-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                <a href="#property-rentals" className="hover:text-amber-600">Property Rentals</a>
              </h4>
              <p className="text-gray-600">
                Discover quality rental properties that meet your budget and lifestyle needs in prime locations across the country.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Bellcrest Properties?</h3>
            <p className="text-lg text-gray-600">Your success is our priority</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line w-6 h-6 flex items-center justify-center text-white"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Trusted Expertise</h4>
              <p className="text-gray-600 text-sm">Years of experience in Kenya's real estate market</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line w-6 h-6 flex items-center justify-center text-white"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Transparent Pricing</h4>
              <p className="text-gray-600 text-sm">Honest, competitive prices with no hidden fees</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-2-line w-6 h-6 flex items-center justify-center text-white"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Personal Service</h4>
              <p className="text-gray-600 text-sm">Dedicated support throughout your property journey</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-2-line w-6 h-6 flex items-center justify-center text-white"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Local Knowledge</h4>
              <p className="text-gray-600 text-sm">Deep understanding of Kenya's property markets</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-amber-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-white mb-4">Ready to Start Your Real Estate Journey?</h3>
          <p className="text-xl text-amber-100 mb-8">
            Contact Bellcrest Properties today and let us help you find the perfect property investment opportunity.
          </p>
          <a 
            href="https://wa.me/254748356920?text=Hello%20Bellcrest%20Properties,%20I%20would%20like%20to%20discuss%20my%20real%20estate%20needs"
            className="bg-white text-amber-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition duration-300 inline-block cursor-pointer whitespace-nowrap"
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact Us on WhatsApp
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h4 className="text-2xl font-bold mb-4" style={{ fontFamily: "Pacifico, serif" }}>
                Bellcrest Properties
              </h4>
              <p className="text-gray-300 mb-4">
                Your trusted partner for real estate investment opportunities across Kenya. 
                We specialize in land sales, property consultation, and helping you make informed investment decisions.
              </p>
              <div className="flex space-x-4">
                <a href="https://wa.me/254748356920" className="text-gray-300 hover:text-white cursor-pointer" target="_blank" rel="noopener noreferrer">
                  <i className="ri-whatsapp-line w-6 h-6 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h5 className="text-lg font-semibold mb-4">Services</h5>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#property-sales" className="hover:text-white">Property Sales</a></li>
                <li><a href="#land-sales" className="hover:text-white">Land Sales</a></li>
                <li><a href="#property-rentals" className="hover:text-white">Property Rentals</a></li>
                <li><a href="#consultation" className="hover:text-white">Consultation</a></li>
              </ul>
            </div>
            
            <div>
              <h5 className="text-lg font-semibold mb-4">Contact</h5>
              <ul className="space-y-2 text-gray-300">
                <li>
                  <a href="https://wa.me/254748356920" className="hover:text-white cursor-pointer" target="_blank" rel="noopener noreferrer">
                    +254 748 356 920
                  </a>
                </li>
                <li>Kenya</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-300">
            <p>&copy; 2024 Bellcrest Properties. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
